import React from 'react';
import { Link } from 'react-router-dom';
import CustomQuoteIntake from '../../components/CustomQuoteIntake';
import Seo from '../../components/Seo';
import { BRAND, HOURS, PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import { ROUTES } from '../../enum/routes';
import { isAdsHost } from '../../utils/host';

const TRUST_ITEMS = [
  { label: 'Licensed local pros', icon: 'shield' as const },
  { label: 'Free estimates', icon: 'estimate' as const },
  { label: 'Fast follow-up, guaranteed', icon: 'clock' as const },
  { label: 'No obligation', icon: 'handshake' as const },
];

function TrustIcon({ name }: { name: (typeof TRUST_ITEMS)[number]['icon'] }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  };

  if (name === 'shield') {
    return (
      <svg {...common}>
        <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
        <path d="M9.5 12l1.8 1.8L15 10" />
      </svg>
    );
  }
  if (name === 'estimate') {
    return (
      <svg {...common}>
        <path d="M8 4h8a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
        <path d="M9 9h6M9 13h6" />
      </svg>
    );
  }
  if (name === 'clock') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4l2.5 2.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M8 11V8a2 2 0 114 0v3" />
      <path d="M12 11V8a2 2 0 114 0v3" />
      <path d="M6 11h12v3a4 4 0 01-4 4h-4a4 4 0 01-4-4v-3z" />
    </svg>
  );
}

export default function AdLandingPage() {
  const adsOnly = isAdsHost();
  const privacyHref = adsOnly ? `${SITE_URL}${ROUTES.PRIVACY}` : ROUTES.PRIVACY;
  const termsHref = adsOnly ? `${SITE_URL}${ROUTES.TERMS}` : ROUTES.TERMS;

  return (
    <div className="ad-landing min-h-screen bg-white text-ink">
      <Seo
        title={`Free Remodel Estimate | ${BRAND}`}
        description="Get a free remodel estimate from Epic Home Pros. Kitchens, bathrooms, ADUs, and outdoor living across Austin, Dallas, Houston, and San Antonio."
        path={adsOnly ? '/' : ROUTES.GET_ESTIMATE}
        noIndex
      />

      <header className="ad-landing__header border-b border-black/5 bg-white">
        <div className="container-page flex items-center justify-between gap-4 py-3 sm:py-4">
          {adsOnly ? (
            <span className="ad-landing__logo shrink-0" aria-label={BRAND}>
              <img src="/assets/img/logo.png" alt={BRAND} width={132} height={116} />
            </span>
          ) : (
            <Link to={ROUTES.HOME} className="ad-landing__logo shrink-0" aria-label={BRAND}>
              <img src="/assets/img/logo.png" alt={BRAND} width={132} height={116} />
            </Link>
          )}
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href={`tel:${PHONE_TEL}`}
              className="hidden items-center gap-2 text-sm font-semibold text-forest hover:text-forest-hover sm:inline-flex"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.8 19.8 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.8 19.8 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
              </svg>
              {PHONE_DISPLAY}
            </a>
            <a href={`tel:${PHONE_TEL}`} className="btn-primary !px-4 !py-2.5 text-sm sm:!px-5">
              Call now
            </a>
          </div>
        </div>
      </header>

      <div className="ad-landing__trust bg-forest text-white">
        <div className="container-page">
          <ul className="ad-landing__trust-list">
            {TRUST_ITEMS.map((item) => (
              <li key={item.label} className="ad-landing__trust-item">
                <span className="ad-landing__trust-icon">
                  <TrustIcon name={item.icon} />
                </span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <section className="ad-landing__stage">
        <div className="ad-landing__layout">
          <aside className="ad-landing__cover ad-landing__cover--left" aria-hidden="true">
            <img
              src="/assets/img/ad-landing/cover-left.png"
              alt=""
              width={364}
              height={735}
              decoding="async"
            />
          </aside>

          <div className="ad-landing__well">
            <div className="ad-landing__center mx-auto text-center">
              {/* <div className="label-eyebrow">Start your project</div> */}
              <h1 className="font-serif mt-3 text-3xl leading-tight text-ink sm:text-4xl lg:text-[2.75rem]">
                Get a free estimate for your home remodel.
              </h1>
              <p className="mx-auto mt-4 max-w-md text-base text-ink/70 sm:text-lg">
                Answer a few quick questions. We'll follow up within one business day and help you take the next step.
              </p>
              <div className="ad-landing__form mt-8 text-left">
                <CustomQuoteIntake />
              </div>
            </div>
          </div>

          <aside className="ad-landing__cover ad-landing__cover--right" aria-hidden="true">
            <img
              src="/assets/img/ad-landing/cover-right.png"
              alt=""
              width={364}
              height={742}
              decoding="async"
            />
          </aside>
        </div>
      </section>

      <footer className="ad-landing__footer border-t border-black/5 bg-white">
        <div className="container-page py-8 sm:py-10">
          <div className="ad-landing__footer-grid">
            <div className="ad-landing__footer-block">
              <div className="ad-landing__footer-label">Serving homeowners in</div>
              <p className="mt-1.5 text-sm font-medium text-ink sm:text-base">
                Austin · Dallas · Houston · San Antonio
              </p>
            </div>
            <div className="ad-landing__footer-block ad-landing__footer-block--center">
              <div className="ad-landing__footer-label">Local pros. Lasting results.</div>
              <p className="mt-1.5 text-sm text-ink/70 sm:text-base">Quality remodels across Texas.</p>
            </div>
            <div className="ad-landing__footer-block ad-landing__footer-block--end">
              <div className="ad-landing__footer-label">Hours</div>
              <p className="mt-1.5 text-sm font-medium text-ink sm:text-base">{HOURS}</p>
              <a href={`tel:${PHONE_TEL}`} className="mt-2 inline-block text-sm font-semibold text-forest hover:text-forest-hover">
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
          <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-black/5 pt-5 text-xs text-ink/45 sm:flex-row">
            <p>
              © {new Date().getFullYear()} {BRAND}
            </p>
            <div className="flex gap-4">
              {adsOnly ? (
                <>
                  <a href={privacyHref} className="hover:text-forest">
                    Privacy
                  </a>
                  <a href={termsHref} className="hover:text-forest">
                    Terms
                  </a>
                </>
              ) : (
                <>
                  <Link to={ROUTES.PRIVACY} className="hover:text-forest">
                    Privacy
                  </Link>
                  <Link to={ROUTES.TERMS} className="hover:text-forest">
                    Terms
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
